import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import AdminWordWheelService from "./AdminWordWheelService";
import {
    SEASON_LEVEL_CATALOG_MIN,
    WORD_WHEEL_DEFAULT_SEASON,
    WORD_WHEEL_SEASON_OPTIONS,
} from "../wordWheelLevelUtils";

/**
 * Re-order (recalculate) puzzle levels 1–1100 for all published puzzles in a season.
 */
export default function WordWheelReorderLevelsDialog({
    open,
    onClose,
    initialSeason = WORD_WHEEL_DEFAULT_SEASON,
    initialLanguage = "english",
    onReordered,
}) {
    const [season, setSeason] = useState(WORD_WHEEL_DEFAULT_SEASON);
    const [language, setLanguage] = useState("english");
    const [publishedCount, setPublishedCount] = useState(null);
    const [countLoading, setCountLoading] = useState(false);
    const [reordering, setReordering] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        if (!open) {
            return;
        }
        const seasonInit =
            initialSeason && WORD_WHEEL_SEASON_OPTIONS.includes(initialSeason)
                ? initialSeason
                : WORD_WHEEL_DEFAULT_SEASON;
        const languageInit =
            initialLanguage && initialLanguage !== "all" ? initialLanguage : "english";
        setSeason(seasonInit);
        setLanguage(languageInit);
        setError(null);
        setSuccess(null);
        setPublishedCount(null);
    }, [open, initialSeason, initialLanguage]);

    useEffect(() => {
        if (!open || !season) {
            return undefined;
        }
        let cancelled = false;
        const load = async () => {
            setCountLoading(true);
            setError(null);
            try {
                const count = await AdminWordWheelService.countPublishedInSeason(season, language);
                if (!cancelled) {
                    setPublishedCount(count);
                }
            } catch (err) {
                if (!cancelled) {
                    setPublishedCount(null);
                    setError(err?.message || "Failed to load published count.");
                }
            } finally {
                if (!cancelled) {
                    setCountLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [open, season, language]);

    const catalogReady =
        publishedCount != null && publishedCount >= SEASON_LEVEL_CATALOG_MIN;

    const handleReorder = async () => {
        setReordering(true);
        setError(null);
        setSuccess(null);
        try {
            const result = await AdminWordWheelService.assignSeasonLevels(season, language);
            const updated = result?.updated ?? 0;
            setSuccess(
                `Re-ordered ${updated} puzzle level(s) in ${season} (${language}). Levels are locked.`
            );
            if (typeof onReordered === "function") {
                onReordered(result);
            }
            const count = await AdminWordWheelService.countPublishedInSeason(season, language);
            setPublishedCount(count);
        } catch (err) {
            setError(err?.message || "Failed to re-order season levels.");
        } finally {
            setReordering(false);
        }
    };

    return (
        <Dialog open={open} onClose={reordering ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Re-order season levels</DialogTitle>
            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                        Recalculate difficulty and assign play levels from 1 (easiest: fewer words) to 1100
                        (hardest). Word-count groups first (4 → 5 → 6 → … → 10), then within each group:
                        shorter longest-word, fewer wheel characters, higher total score (common/easy words).
                        Existing levels are overwritten and then locked for every published puzzle in the
                        selected season.
                    </Typography>

                    <FormControl size="small" fullWidth>
                        <InputLabel id="ww-reorder-season-label">Season</InputLabel>
                        <Select
                            labelId="ww-reorder-season-label"
                            label="Season"
                            value={season}
                            onChange={(e) => setSeason(e.target.value)}
                            disabled={reordering}
                        >
                            {WORD_WHEEL_SEASON_OPTIONS.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" fullWidth>
                        <InputLabel id="ww-reorder-language-label">Language</InputLabel>
                        <Select
                            labelId="ww-reorder-language-label"
                            label="Language"
                            value={language}
                            onChange={(e) => setLanguage(e.target.value)}
                            disabled={reordering}
                        >
                            <MenuItem value="english">english</MenuItem>
                            <MenuItem value="french">french</MenuItem>
                        </Select>
                    </FormControl>

                    {countLoading && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}

                    {!countLoading && publishedCount != null && (
                        <Alert severity={catalogReady ? "info" : "warning"}>
                            {publishedCount} published puzzle(s) in {season} ({language}).
                            {!catalogReady &&
                                ` Need at least ${SEASON_LEVEL_CATALOG_MIN} before re-ordering.`}
                        </Alert>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}
                    {success && <Alert severity="success">{success}</Alert>}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={reordering}>
                    Close
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleReorder}
                    disabled={countLoading || reordering || !catalogReady}
                    startIcon={reordering ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    Re-order levels
                </Button>
            </DialogActions>
        </Dialog>
    );
}
